/* The fund's RECORD START (Ale ruling 2026-09-04) — the date the
 * depositary's record of this fund begins, read off the acceptance
 * document and editable at onboarding by a manager. The doctrine, in the
 * same words the engine uses (nolos.funds.record_start):
 *
 *   · nothing dated BEFORE it is demanded or chased;
 *   · movements after it need their transaction documents;
 *   · a balance dated ON OR BEFORE it is the inception balance — the
 *     position's starting line, not a movement.
 *
 * A fund without a record start demands everything — every predicate
 * here is false on a missing date. Dates are ISO day strings
 * (YYYY-MM-DD), which compare correctly as strings. No imports: this
 * file is compiled on its own by `npm run test:unit`. */

const ISO_DAY = /^\d{4}-\d{2}-\d{2}$/

/** A well-formed ISO day — what the inline date field may hand the API. */
export function isIsoDay(v: string | null | undefined): v is string {
  if (!v || !ISO_DAY.test(v)) return false
  const [y, m, d] = v.split('-').map(Number)
  if (m < 1 || m > 12 || d < 1) return false
  return d <= new Date(Date.UTC(y, m, 0)).getUTCDate()
}

/** Strictly before the record start: not demanded, never chased. */
export function isBeforeRecordStart(
  start: string | null | undefined,
  when: string | null | undefined
): boolean {
  if (!isIsoDay(start) || !isIsoDay(when)) return false
  return when < start
}

/** On or before the record start: the inception-balance rule. */
export function isOnOrBeforeRecordStart(
  start: string | null | undefined,
  when: string | null | undefined
): boolean {
  if (!isIsoDay(start) || !isIsoDay(when)) return false
  return when <= start
}

/** What the facts row says beside a set date — one clause, no second
 *  sentence: the rule the date enforces. */
export const RECORD_START_RULE = 'nothing dated before it is demanded or chased'

/** The value the API is sent from the inline field: a well-formed day, or
 *  null to clear (an emptied field clears the record start). */
export function recordStartPayload(draft: string): string | null {
  const v = draft.trim()
  if (v === '') return null
  return isIsoDay(v) ? v : null
}
